import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import GameShell from '../../shared/ui/GameShell';
import LoadingScreen from '../../shared/ui/LoadingScreen';
import ErrorScreen from '../../shared/ui/ErrorScreen';
import FinishedDaily from '../../shared/ui/FinishedDaily';
import { finish as recordOutcome, isFinished } from '../../shared/dailySession';
import { useDailySnapshot, usePersistedState } from '../../shared/useDailySession';
import DailyDoneOverlay from '../../shared/ui/DailyDoneOverlay';
import { loadTodaysPuzzle, formatDateLabel } from '../../shared/daily';
import { isValidWordFull } from '../../shared/dictionary';
import type { WordCircleLevel } from './types';
import './wordCircle.css';

export default function WordCircleGame() {
  const [data, setData] = useState<{
    level: WordCircleLevel;
    dateKey: string;
    isToday: boolean;
  } | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    loadTodaysPuzzle<WordCircleLevel>('wordCircle')
      .then((res) => {
        if (cancelled) return;
        if (!res) setLoadError('No daily puzzle published for Word Circle yet.');
        else setData({ level: res.data, dateKey: res.dateKey, isToday: res.isToday });
      })
      .catch((e) => !cancelled && setLoadError(String(e?.message ?? e)));
    return () => {
      cancelled = true;
    };
  }, []);

  if (loadError) return <ErrorScreen message={loadError} gameId="wordCircle" />;
  if (!data) {
    return (
      <GameShell gameId="wordCircle">
        <LoadingScreen label="Fetching today's puzzle…" />
      </GameShell>
    );
  }

  // A daily is one attempt. Once the round has resolved, show the result
  // instead of mounting a fresh board.
  if (isFinished('wordCircle', data.dateKey)) {
    return (
      <FinishedDaily gameId="wordCircle" dateKey={data.dateKey} isToday={data.isToday} />
    );
  }

  return (
    <WordCirclePlay
      key={data.dateKey}
      level={data.level}
      dateKey={data.dateKey}
      isToday={data.isToday}
    />
  );
}

function WordCirclePlay({
  level,
  dateKey,
  isToday,
}: {
  level: WordCircleLevel;
  dateKey: string;
  isToday: boolean;
}) {
  const letters = useMemo(() => level.letters.map((l) => l.toUpperCase()), [level]);
  const placements = useMemo(
    () => level.placements.map((p) => ({ ...p, word: p.word.toUpperCase() })),
    [level],
  );
  const bonusWords = useMemo(
    () => new Set((level.bonusWords ?? []).map((w) => w.toUpperCase())),
    [level],
  );

  const { restored, save } = useDailySnapshot<{
    found: string[];
    bonus: string[];
    hintUsed: boolean;
  }>('wordCircle', dateKey);

  const [order, setOrder] = useState<number[]>(() => letters.map((_, i) => i));
  const [path, setPath] = useState<number[]>([]);
  const [foundWords, setFoundWords] = useState<Set<string>>(
    () => new Set(restored?.found ?? []),
  );
  const [foundBonus, setFoundBonus] = useState<Set<string>>(
    () => new Set(restored?.bonus ?? []),
  );
  const [toast, setToast] = useState<{ msg: string; style: 'success' | 'error' | 'info' } | null>(null);
  const [completed, setCompleted] = useState(false);
  const [hintUsed, setHintUsed] = useState(restored?.hintUsed ?? false);

  usePersistedState(
    save,
    { found: [...foundWords], bonus: [...foundBonus], hintUsed },
    !completed,
  );

  useEffect(() => {
    if (completed) recordOutcome('wordCircle', dateKey, 'won', { subtitle: `Found all ${placements.length} words.` });
  }, [completed, dateKey]);

  const currentWord = useMemo(() => path.map((i) => letters[i]).join(''), [path, letters]);

  const showToast = useCallback((msg: string, style: 'success' | 'error' | 'info') => {
    setToast({ msg, style });
    setTimeout(() => setToast(null), 1400);
  }, []);

  const checkCompletion = useCallback(
    (foundSet: Set<string>) => {
      if (placements.every((p) => foundSet.has(p.word))) setCompleted(true);
    },
    [placements],
  );

  const submitWord = useCallback(async () => {
    const word = currentWord;
    setPath([]);
    if (word.length < 3) return;

    const targetMatch = placements.find((p) => p.word === word);
    if (targetMatch && !foundWords.has(word)) {
      const next = new Set(foundWords).add(word);
      setFoundWords(next);
      showToast(`✓ ${word}`, 'success');
      checkCompletion(next);
      return;
    }

    if (foundWords.has(word) || foundBonus.has(word)) {
      showToast('Already found', 'info');
      return;
    }

    if (bonusWords.has(word)) {
      setFoundBonus((prev) => new Set(prev).add(word));
      showToast('Bonus!', 'success');
      return;
    }

    const valid = await isValidWordFull(word);
    if (valid) {
      setFoundBonus((prev) => new Set(prev).add(word));
      showToast('Bonus!', 'success');
    } else {
      showToast('Not a word', 'error');
    }
  }, [currentWord, placements, foundWords, foundBonus, bonusWords, showToast, checkCompletion]);

  const shuffle = useCallback(() => {
    setOrder((prev) => {
      const a = [...prev];
      for (let i = a.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [a[i], a[j]] = [a[j], a[i]];
      }
      return a;
    });
  }, []);

  const revealWord = useCallback(() => {
    if (hintUsed) return;
    const unfound = placements.filter((p) => !foundWords.has(p.word));
    if (unfound.length === 0) return;
    const pick = unfound[Math.floor(Math.random() * unfound.length)];
    const next = new Set(foundWords).add(pick.word);
    setFoundWords(next);
    setHintUsed(true);
    showToast(`Revealed: ${pick.word}`, 'info');
    checkCompletion(next);
  }, [hintUsed, placements, foundWords, showToast, checkCompletion]);

  const actions = (
    <button
      className="btn btn-secondary"
      onClick={revealWord}
      disabled={completed || hintUsed || foundWords.size === placements.length}
      title="Reveal a word (one per puzzle)"
    >
      ✨ Reveal
    </button>
  );

  return (
    <GameShell
      gameId="wordCircle"
      actions={actions}
      subtitle={`${formatDateLabel(dateKey, isToday)} • ${foundWords.size}/${placements.length} found`}
    >
      <div className="wc-board">
        <WordColumnsGrid placements={placements} foundWords={foundWords} />

        <div className="wc-current-word">
          {currentWord ? (
            <div className="wc-word-pills">
              {currentWord.split('').map((ch, i) => (
                <span key={i} className="wc-word-pill">
                  {ch}
                </span>
              ))}
            </div>
          ) : (
            <span className="wc-word-placeholder">Connect letters</span>
          )}
        </div>

        <LetterCircle
          letters={letters}
          order={order}
          path={path}
          onPath={setPath}
          onEnd={submitWord}
        />

        <div className="wc-controls">
          <button className="wc-ctrl" onClick={shuffle} aria-label="Shuffle letters">
            ⇄
          </button>
          <div className="wc-bonus-count">
            {foundBonus.size > 0 ? `★ ${foundBonus.size} bonus` : ''}
          </div>
        </div>
      </div>

      {toast && <div className={`wc-toast wc-toast-${toast.style}`}>{toast.msg}</div>}

      {completed && (
        <DailyDoneOverlay
          dateKey={dateKey}
          isToday={isToday}
          subtitle={`Found all ${placements.length} words${foundBonus.size > 0 ? ` + ${foundBonus.size} bonus` : ''}.`}
        />
      )}
    </GameShell>
  );
}

function WordColumnsGrid({
  placements,
  foundWords,
}: {
  placements: { word: string }[];
  foundWords: Set<string>;
}) {
  const sorted = [...placements].sort((a, b) => a.word.localeCompare(b.word));
  const half = Math.ceil(sorted.length / 2);
  const columns = [sorted.slice(0, half), sorted.slice(half)];
  // iOS derives cell size from available width capped at 26 so long words never
  // overflow; the CSS does the same, driven by the longest word here.
  const maxWordLength = sorted.reduce((m, p) => Math.max(m, p.word.length), 1);

  return (
    <div
      className="wc-columns"
      style={{ ['--wc-maxlen' as string]: String(maxWordLength) }}
    >
      {columns.map((col, ci) => (
        <div key={ci} className="wc-column">
          {col.map((p) => {
            const found = foundWords.has(p.word);
            return (
              <div key={p.word} className={'wc-word-row' + (found ? ' is-found' : '')}>
                {p.word.split('').map((ch, i) => (
                  <span
                    key={i}
                    className={'wc-word-cell' + (found || i === 0 ? ' is-revealed' : '')}
                  >
                    {found || i === 0 ? ch : ''}
                  </span>
                ))}
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}

function LetterCircle({
  letters,
  order,
  path,
  onPath,
  onEnd,
}: {
  letters: string[];
  order: number[];
  path: number[];
  onPath: (next: number[]) => void;
  onEnd: () => void;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState(320);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      for (const e of entries) setSize(Math.floor(e.contentRect.width));
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Geometry mirrors LetterCircleView: the wheel is 82% of the box, letters sit
  // on a ring at 34% of the wheel, and each bubble is the tap target plus 8.
  const center = size / 2;
  const bgDiameter = size * 0.82;
  const ringRadius = bgDiameter * 0.34;
  const tapSize = Math.max(50, bgDiameter * 0.2);
  const tileSize = tapSize + 8;
  const fontSize = Math.max(34, bgDiameter * 0.17);
  const count = letters.length;

  const positions = useMemo(
    () =>
      order.map((_, displayIdx) => {
        const angle = (2 * Math.PI * displayIdx) / count - Math.PI / 2;
        return {
          x: center + ringRadius * Math.cos(angle),
          y: center + ringRadius * Math.sin(angle),
        };
      }),
    [order, count, center, ringRadius],
  );

  const displayIndexFor = useCallback(
    (actualIdx: number) => {
      for (let i = 0; i < order.length; i++) {
        if (order[i] === actualIdx) return i;
      }
      return actualIdx;
    },
    [order],
  );

  const handlePointer = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      const rect = containerRef.current?.getBoundingClientRect();
      if (!rect) return;
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      for (let displayIdx = 0; displayIdx < positions.length; displayIdx++) {
        const p = positions[displayIdx];
        const dist = Math.hypot(x - p.x, y - p.y);
        if (dist < tileSize / 2 + 10) {
          const actualIdx = order[displayIdx];
          if (path.length === 0) {
            onPath([actualIdx]);
          } else if (!path.includes(actualIdx)) {
            onPath([...path, actualIdx]);
          }
          return;
        }
      }
    },
    [positions, order, path, onPath, tileSize],
  );

  const onPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    (e.currentTarget as HTMLDivElement).setPointerCapture(e.pointerId);
    handlePointer(e);
  };
  const onPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (e.buttons === 0 && e.pointerType === 'mouse') return;
    handlePointer(e);
  };
  const onPointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
    try {
      (e.currentTarget as HTMLDivElement).releasePointerCapture(e.pointerId);
    } catch {}
    onEnd();
  };

  return (
    <div
      ref={containerRef}
      className="wc-circle"
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
      style={{ touchAction: 'none' }}
    >
      <div
        className="wc-circle-bg"
        style={{
          width: size * 0.82,
          height: size * 0.82,
          left: center - (size * 0.82) / 2,
          top: center - (size * 0.82) / 2,
        }}
      />
      {path.length >= 2 && (
        <svg className="wc-circle-lines" viewBox={`0 0 ${size} ${size}`}>
          {/* Black keyline underlay with the colour laid on top — the brutalist
              "pipe" the iOS connectionLines Canvas draws. */}
          {[
            { stroke: 'var(--ink)', width: 14 },
            { stroke: 'var(--wc-selection)', width: 8 },
          ].map(({ stroke, width }) => (
            <polyline
              key={width}
              points={path
                .map((actualIdx) => {
                  const p = positions[displayIndexFor(actualIdx)];
                  return `${p.x},${p.y}`;
                })
                .join(' ')}
              fill="none"
              stroke={stroke}
              strokeWidth={width}
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          ))}
        </svg>
      )}
      {order.map((actualIdx, displayIdx) => {
        const pos = positions[displayIdx];
        const inPath = path.includes(actualIdx);
        return (
          <div
            key={displayIdx}
            className={'wc-letter' + (inPath ? ' is-active' : '')}
            style={{
              width: tileSize,
              height: tileSize,
              left: pos.x - tileSize / 2,
              top: pos.y - tileSize / 2,
              fontSize,
            }}
          >
            {letters[actualIdx]}
          </div>
        );
      })}
    </div>
  );
}
